import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicProspectusAPI, resolveAssetUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Award,
  Building2,
  CheckCircle2,
  Loader2,
  Calendar,
  MapPin,
  Clock,
  Sparkles,
  ShieldCheck,
  FileSignature,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast, Toaster } from 'sonner';

const COMPLIANCE_CLAUSES = [
  ['Educational independence', 'The Sponsor acknowledges that the educational content of the activity is determined exclusively by the issuing organisation. The Sponsor has no involvement in the selection of topics, speakers or any clinical content.'],
  ['No inducement', 'No part of any sponsorship contribution will be paid to, or applied for the personal benefit of, any individual healthcare practitioner as an inducement to prescribe, recommend, supply or use any therapeutic good.'],
  ['Modest hospitality', 'Any hospitality provided in connection with the activity will be modest, secondary to the educational purpose, and consistent with applicable industry codes (Medicines Australia Code of Conduct, MTAA Code, and AHPRA / Medical Board of Australia advertising guidelines).'],
  ['Therapeutic Goods Act compliance', 'The Sponsor undertakes that any product information, materials or branding provided in connection with this activity will comply with the Therapeutic Goods Act 1989 (Cth) and the Therapeutic Goods Advertising Code, including restrictions on the advertising of prescription medicines to the public.'],
  ['Transparency &amp; reporting', 'The issuing organisation reserves the right to disclose the Sponsor relationship in attendee communications and reports, in accordance with industry transparency expectations.'],
  ['No promise of attendance', 'Sponsorship of this activity does not entitle the Sponsor to direct contact details of individual attendees, nor any guarantee of attendance numbers.'],
  ['Termination', 'The issuing organisation may terminate this sponsorship arrangement if continuation would, in its reasonable opinion, breach any applicable code, statute or its own ethical obligations. Reasonable pro-rata refunds will be considered in such circumstances.'],
];

export default function PublicSponsorshipProspectus() {
  const { inviteCode } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    sponsor_org: '',
    abn: '',
    contact_name: '',
    contact_title: '',
    contact_email: '',
    contact_phone: '',
    tier_name: '',
    contribution_amount: '',
    payment_intent: 'will_pay',
    is_iida_member: false,
    comments: '',
    signature_name: '',
    agreed: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await publicProspectusAPI.get(inviteCode);
        setEvent(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'This sponsorship link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [inviteCode]);

  const tiers = event?.sponsorship_tiers || [];
  const sortedTiers = [...tiers].sort((a, b) => (b.amount || 0) - (a.amount || 0));
  const branding = event?.branding || {};
  const primary = branding.primary_color || '#0F172A';

  const selectedTier = sortedTiers.find((t) => t.tier_name === form.tier_name);

  // Pre-fill contribution amount when tier changes
  const onTierChange = (tierName) => {
    const t = sortedTiers.find((x) => x.tier_name === tierName);
    setForm((p) => ({
      ...p,
      tier_name: tierName,
      contribution_amount: t?.amount > 0 ? String(t.amount) : p.contribution_amount,
    }));
  };

  // Computed discount preview
  const rawAmount = parseFloat(form.contribution_amount) || 0;
  let discount = 0;
  if (form.is_iida_member) {
    const pct = parseFloat(event?.iida_member_discount_percent) || 0;
    const fixed = parseFloat(event?.iida_member_discount_amount) || 0;
    if (pct > 0) discount = Math.round(rawAmount * pct) / 100;
    else if (fixed > 0) discount = Math.min(fixed, rawAmount);
  }
  const netAmount = Math.max(0, rawAmount - discount);
  const hasDiscountConfigured =
    (parseFloat(event?.iida_member_discount_percent) || 0) > 0 ||
    (parseFloat(event?.iida_member_discount_amount) || 0) > 0;

  const validate = () => {
    if (!form.sponsor_org.trim()) return 'Please enter your organisation name.';
    if (!form.contact_name.trim()) return 'Please enter the authorised contact name.';
    if (!form.contact_email.trim() || !form.contact_email.includes('@')) return 'A valid email is required.';
    if (!form.tier_name) return 'Please select a tier of interest.';
    if (!rawAmount || rawAmount <= 0) return 'Please enter your intended contribution amount.';
    if (!form.signature_name.trim()) return 'Please type your full name as a digital signature.';
    if (!form.agreed) return 'Please tick the agreement checkbox.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) {
      toast.error(msg);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        sponsor_org: form.sponsor_org.trim(),
        abn: form.abn.trim(),
        contact_name: form.contact_name.trim(),
        contact_title: form.contact_title.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
        tier_name: form.tier_name,
        contribution_amount: rawAmount,
        payment_intent: form.payment_intent,
        is_iida_member: form.is_iida_member,
        comments: form.comments.trim(),
        signature_name: form.signature_name.trim(),
        agreed: true,
      };
      await publicProspectusAPI.submitEOI(inviteCode, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Link not available</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-lg w-full" data-testid="eoi-submitted-card">
          <CardHeader className="text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <CardTitle>Expression of interest received</CardTitle>
            <CardDescription>
              Thank you for your interest in supporting <strong>{event.title}</strong>. Our committee
              will review your submission and respond directly via <strong>{form.contact_email}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center text-sm text-muted-foreground">
            You may now close this window.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-prospectus-page">
      <Toaster richColors position="top-center" />

      {/* Hero header */}
      <div
        className="border-b border-border"
        style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}CC 60%, ${branding.accent_color || '#0EA5E9'} 100%)` }}
      >
        <div className="max-w-4xl mx-auto px-6 py-10 text-white">
          <div className="flex items-start gap-4 flex-wrap">
            {branding.logo_url && (
              <img
                src={resolveAssetUrl(branding.logo_url)}
                alt={branding.organization_name}
                className="h-16 w-16 rounded-lg bg-white p-2 object-contain shadow-md"
                data-testid="prospectus-logo"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest text-white/80 font-semibold">
                Sponsorship Prospectus
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold mt-1" data-testid="prospectus-event-title">
                {event.title}
              </h1>
              <p className="text-white/90 mt-2 max-w-2xl">
                An invitation from <strong>{event.org_name || branding.organization_name}</strong> to
                partner in delivering independent, evidence-based continuing professional development.
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Event at a glance */}
        <Card className="border-border" data-testid="prospectus-at-a-glance">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              The educational activity
            </CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="flex gap-3">
              <Calendar className="w-4 h-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground text-xs">Date</p>
                <p className="font-medium">{format(parseISO(event.date), 'EEEE, d MMMM yyyy')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Clock className="w-4 h-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground text-xs">Time &amp; duration</p>
                <p className="font-medium">{event.time} · {event.duration_hours} hours</p>
              </div>
            </div>
            <div className="sm:col-span-2 flex gap-3">
              <MapPin className="w-4 h-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground text-xs">Venue</p>
                <p className="font-medium">{event.venue}</p>
              </div>
            </div>
            {event.description && (
              <div className="sm:col-span-2 mt-2">
                <p className="text-sm leading-relaxed whitespace-pre-line">{event.description}</p>
              </div>
            )}
            {event.learning_objectives?.length > 0 && (
              <div className="sm:col-span-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1 mt-2">Learning objectives</p>
                <ul className="text-sm space-y-1 list-disc pl-5">
                  {event.learning_objectives.map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </div>
            )}
            {event.topics?.length > 0 && (
              <div className="sm:col-span-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1 mt-2">Programme topics</p>
                <div className="flex flex-wrap gap-2">
                  {event.topics.map((t, i) => (
                    <Badge key={i} variant="outline">{t}</Badge>
                  ))}
                </div>
              </div>
            )}
            {event.speakers?.length > 0 && (
              <div className="sm:col-span-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1 mt-2">Faculty &amp; speakers</p>
                <ul className="text-sm space-y-1">
                  {event.speakers.map((s, i) => (
                    <li key={i}>
                      <span className="font-medium">{s.name}</span>
                      {s.title || s.organization ? <span className="text-muted-foreground"> — {[s.title, s.organization].filter(Boolean).join(', ')}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sponsorship tiers */}
        <Card className="border-border" data-testid="prospectus-tiers-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5" />
              Partnership opportunities
            </CardTitle>
            <CardDescription>
              Select the level of partnership that best suits your organisation. All contributions
              are applied exclusively to the reasonable costs of organising and delivering the
              educational activity.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sortedTiers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tiers have been configured for this event yet — please contact the organising committee.</p>
            ) : (
              <div className="grid gap-3" data-testid="prospectus-tier-list">
                {sortedTiers.map((t, idx) => {
                  const isTop = idx === 0 && sortedTiers.length > 1;
                  return (
                    <div
                      key={`${t.tier_name}-${idx}`}
                      className={`p-4 rounded-lg border ${isTop ? 'border-amber-400 bg-amber-50/30' : 'border-border bg-muted/20'}`}
                      data-testid={`prospectus-tier-${idx}`}
                    >
                      <div className="flex justify-between items-start gap-3 flex-wrap">
                        <div>
                          <p className={`text-xs uppercase tracking-wide ${isTop ? 'text-amber-700' : 'text-muted-foreground'} font-semibold`}>
                            {isTop ? 'Featured partner' : `Tier ${idx + 1} of ${sortedTiers.length}`}
                          </p>
                          <h3 className="text-lg font-bold mt-0.5">{t.tier_name}</h3>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">
                            {t.amount > 0
                              ? `$${Number(t.amount).toLocaleString('en-AU')}`
                              : 'By negotiation'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t.amount > 0 ? 'AUD · ex-GST' : 'Bespoke arrangement'}
                          </p>
                        </div>
                      </div>
                      {(t.benefits || []).length > 0 && (
                        <ul className="text-sm mt-3 space-y-1 list-disc pl-5">
                          {(t.benefits || []).map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Schedule A — compliance */}
        <Card className="border-border" data-testid="prospectus-schedule-a">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Schedule A — Terms &amp; Compliance
            </CardTitle>
            <CardDescription>
              Standard clauses modelled on the Medicines Australia Code of Conduct (Ed. 20), the MTAA
              Code, the Therapeutic Goods Act 1989 (Cth), and AHPRA / Medical Board of Australia
              advertising guidelines.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm leading-relaxed list-decimal pl-5">
              {COMPLIANCE_CLAUSES.map(([head, body], i) => (
                <li key={i}>
                  <span className="font-semibold">{head}.</span>
                  <span className="text-muted-foreground"> </span>
                  <span dangerouslySetInnerHTML={{ __html: body }} />
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {/* EOI form */}
        <Card className="border-border" data-testid="prospectus-eoi-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSignature className="w-5 h-5" />
              Expression of Interest
            </CardTitle>
            <CardDescription>
              Submit your provisional interest in sponsoring this activity. The IIDA committee will
              respond directly to your nominated contact email. This is not a binding agreement —
              a formal sponsorship letter will follow on approval.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-6" onSubmit={handleSubmit} data-testid="prospectus-eoi-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="eoi-org">Sponsor organisation *</Label>
                  <Input
                    id="eoi-org"
                    placeholder="Company / institution name"
                    value={form.sponsor_org}
                    onChange={(e) => setForm({ ...form, sponsor_org: e.target.value })}
                    data-testid="eoi-org-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eoi-abn">ABN / ACN</Label>
                  <Input
                    id="eoi-abn"
                    placeholder="12 345 678 901"
                    value={form.abn}
                    onChange={(e) => setForm({ ...form, abn: e.target.value })}
                    data-testid="eoi-abn-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eoi-title">Position / title</Label>
                  <Input
                    id="eoi-title"
                    placeholder="e.g. Marketing Director"
                    value={form.contact_title}
                    onChange={(e) => setForm({ ...form, contact_title: e.target.value })}
                    data-testid="eoi-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eoi-name">Authorised representative *</Label>
                  <Input
                    id="eoi-name"
                    placeholder="Full name"
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    data-testid="eoi-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eoi-email">Email *</Label>
                  <Input
                    id="eoi-email"
                    type="email"
                    placeholder="name@company.com"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    data-testid="eoi-email-input"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="eoi-phone">Phone</Label>
                  <Input
                    id="eoi-phone"
                    placeholder="+61 ..."
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    data-testid="eoi-phone-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="eoi-tier">Tier of interest *</Label>
                  <Select value={form.tier_name} onValueChange={onTierChange}>
                    <SelectTrigger id="eoi-tier" data-testid="eoi-tier-select">
                      <SelectValue placeholder="Select a tier" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedTiers.map((t, i) => (
                        <SelectItem key={`${t.tier_name}-${i}`} value={t.tier_name}>
                          {t.tier_name}{t.amount > 0 ? ` — $${Number(t.amount).toLocaleString('en-AU')}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eoi-amount">Contribution amount (AUD) *</Label>
                  <Input
                    id="eoi-amount"
                    type="number"
                    min="0"
                    step="100"
                    value={form.contribution_amount}
                    onChange={(e) => setForm({ ...form, contribution_amount: e.target.value })}
                    data-testid="eoi-amount-input"
                  />
                </div>
              </div>

              {/* IIDA member toggle */}
              <div className="rounded-lg border border-border p-4 bg-muted/30 space-y-3" data-testid="eoi-iida-member-block">
                <Label className="font-medium">Are you an IIDA member?</Label>
                <div className="flex items-center gap-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="iida-member"
                      checked={form.is_iida_member === true}
                      onChange={() => setForm({ ...form, is_iida_member: true })}
                      data-testid="eoi-iida-member-yes"
                    />
                    <span className="text-sm">Yes, IIDA member</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="iida-member"
                      checked={form.is_iida_member === false}
                      onChange={() => setForm({ ...form, is_iida_member: false })}
                      data-testid="eoi-iida-member-no"
                    />
                    <span className="text-sm">Non-member</span>
                  </label>
                </div>
                {form.is_iida_member && hasDiscountConfigured && discount > 0 && (
                  <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2" data-testid="eoi-discount-preview">
                    <strong>IIDA member discount applied:</strong> ${discount.toLocaleString('en-AU', { minimumFractionDigits: 2 })} off.
                    Net contribution: <strong>${netAmount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</strong> AUD.
                  </div>
                )}
                {form.is_iida_member && !hasDiscountConfigured && (
                  <p className="text-xs text-muted-foreground">No member discount is configured for this event.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Payment intent</Label>
                <div className="flex items-center gap-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="payment-intent"
                      checked={form.payment_intent === 'will_pay'}
                      onChange={() => setForm({ ...form, payment_intent: 'will_pay' })}
                      data-testid="eoi-payment-intent-will"
                    />
                    <span className="text-sm">I will pay shortly</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="payment-intent"
                      checked={form.payment_intent === 'paid'}
                      onChange={() => setForm({ ...form, payment_intent: 'paid' })}
                      data-testid="eoi-payment-intent-paid"
                    />
                    <span className="text-sm">I have already paid</span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="eoi-comments">Comments (optional)</Label>
                <Textarea
                  id="eoi-comments"
                  rows={3}
                  placeholder="Anything you'd like the committee to know"
                  value={form.comments}
                  onChange={(e) => setForm({ ...form, comments: e.target.value })}
                  data-testid="eoi-comments-input"
                />
              </div>

              {/* Digital signature */}
              <div className="space-y-2 rounded-lg border border-border p-4 bg-muted/30">
                <Label htmlFor="eoi-signature">Digital signature *</Label>
                <p className="text-xs text-muted-foreground">
                  Type your full legal name to digitally sign this expression of interest.
                </p>
                <Input
                  id="eoi-signature"
                  placeholder="Full legal name"
                  value={form.signature_name}
                  onChange={(e) => setForm({ ...form, signature_name: e.target.value })}
                  className="font-serif italic text-lg"
                  data-testid="eoi-signature-input"
                />
                <div className="flex items-start gap-2 pt-2">
                  <Checkbox
                    id="eoi-agree"
                    checked={form.agreed}
                    onCheckedChange={(v) => setForm({ ...form, agreed: !!v })}
                    data-testid="eoi-agree-checkbox"
                  />
                  <Label htmlFor="eoi-agree" className="text-sm font-normal leading-snug">
                    I have read and agree to the Schedule A terms &amp; compliance above. I confirm I am
                    authorised to submit this expression of interest on behalf of the sponsor organisation.
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full gap-2"
                data-testid="eoi-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Building2 className="w-4 h-4" />
                )}
                Submit Expression of Interest
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-center">
          Issued by {event.org_name || branding.organization_name}{branding.abn ? ` · ABN ${branding.abn}` : ''}{branding.contact_email ? ` · ${branding.contact_email}` : ''}
        </p>
      </main>
    </div>
  );
}
